import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminNav from './AdminNav';
import './Manager.css';
import './AdminPanel.css';
import { API_BASE } from '../config/api';

const ProductsManager = ({ onUpdate }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    subcategories: '',
    categoryLabel: '',
    price: '',
    originalPrice: '',
    description: '',
    images: [],
    badge: '',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    stock: 0,
    isActive: true,
    isFeatured: false,
    showcaseSection: '',
    sizeType: 'NONE',
    availableSizes: [],
    colorVariants: []
  });
  const [loading, setLoading] = useState(false);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [newColor, setNewColor] = useState({ colorName: '', colorHex: '#000000' });
  const [uploadingColorIndex, setUploadingColorIndex] = useState(null);

  // Subcategory options for each category - matches menuData structure
  const subcategoryOptions = {
    'Women': [
      'Tops & T-Shirts', 'Dresses', 'Jeans & Trousers', 'Skirts & Shorts',
      'Sweaters & Jackets', 'Lingerie & Sleepwear', 'Heels', 'Flats',
      'Sandals', 'Sneakers', 'Boots', 'Jewelry', 'Bags & Purses',
      'Watches', 'Sunglasses', 'Scarves & Hats', 'Hair Accessories'
    ],
    'Men': [
      'T-Shirts & Polos', 'Shirts', 'Jeans', 'Trousers & Shorts',
      'Jackets & Coats', 'Sweatshirts & Hoodies', 'Innerwear & Sleepwear',
      'Casual Shoes', 'Formal Shoes', 'Sneakers', 'Sandals & Slides',
      'Boots', 'Watches', 'Wallets', 'Belts', 'Sunglasses', 'Caps & Hats', 'Socks'
    ],
    'Kids': [
      'Boys\' Clothing', 'Boys\' Footwear', 'Girls\' Clothing',
      'Girls\' Footwear', 'Toys & Games', 'Baby Essentials'
    ],
    'Bags & Luggage': [
      'Backpacks', 'Handbags', 'Sling Bags',
      'Laptop Bags', 'Trolley Bags', 'Wallets & Clutches'
    ],
    'Beauty & Personal Care': [
      'Skincare', 'Haircare', 'Makeup', 'Fragrances', 'Bath & Body'
    ],
    'Electronics & Gadgets': [],
    'Home & Living': [
      'Home Décor', 'Kitchen & Dining', 'Lighting'
    ],
    'Sports & Outdoors': [
      'Fitness Equipment', 'Sportswear'
    ],
    'Unisex': [
      'Clothing', 'Footwear', 'Bags & Accessories', 
      'Jewelry & Ornaments', 'Hats', 'Sunglasses & Headwear'
    ]
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/products?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setProducts(data.data || []);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error fetching products:', error);
      alert('Error loading products');
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCategories(data.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // If category changes, reset subcategories
    if (name === 'category') {
      setSelectedSubcategories([]);
      setFormData(prev => ({
        ...prev,
        category: value,
        subcategories: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubcategoryToggle = (subcategory) => {
    const newSelected = selectedSubcategories.includes(subcategory)
      ? selectedSubcategories.filter(s => s !== subcategory)
      : [...selectedSubcategories, subcategory];
    
    setSelectedSubcategories(newSelected);
    setFormData(prev => ({
      ...prev,
      subcategories: newSelected.join(', ')
    }));
  };

  const handleSizeToggle = (size) => {
    const newSizes = formData.availableSizes.includes(size)
      ? formData.availableSizes.filter(s => s !== size)
      : [...formData.availableSizes, size];
    
    setFormData(prev => ({
      ...prev,
      availableSizes: newSizes
    }));
  };

  const handleSizeTypeChange = (e) => {
    const newSizeType = e.target.value;
    setFormData(prev => ({
      ...prev,
      sizeType: newSizeType,
      availableSizes: [] // Reset sizes when type changes
    }));
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + formData.images.length > 6) {
      alert('Maximum 6 images allowed');
      return;
    }
    setSelectedFiles(files);
    // Auto-upload immediately after selection
    if (files.length > 0) {
      uploadImages(files);
    }
  };

  const uploadImages = async (files) => {
    setUploadingImages(true);
    try {
      const formDataToSend = new FormData();
      files.forEach(file => {
        formDataToSend.append('images', file);
      });

      console.log('Uploading images to:', `${API_BASE}/products/upload-images`);
      console.log('Number of files:', files.length);

      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/products/upload-images`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataToSend
      });

      const data = await response.json();
      console.log('Upload response:', data);
      
      if (response.ok) {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, ...data.imageUrls]
        }));
        setSelectedFiles([]);
        // Clear file input
        const fileInput = document.getElementById('productImages');
        if (fileInput) fileInput.value = '';
        alert(`${data.imageUrls.length} image(s) uploaded successfully!`);
      } else {
        console.error('Upload failed:', data);
        alert('Error uploading images: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Error uploading images: ' + error.message);
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const addColorVariant = () => {
    if (!newColor.colorName.trim()) {
      alert('Please enter a color name');
      return;
    }
    setFormData(prev => ({
      ...prev,
      colorVariants: [...prev.colorVariants, { colorName: newColor.colorName.trim(), colorHex: newColor.colorHex, images: [] }]
    }));
    setNewColor({ colorName: '', colorHex: '#000000' });
  };

  const removeColorVariant = (index) => {
    setFormData(prev => ({
      ...prev,
      colorVariants: prev.colorVariants.filter((_, i) => i !== index)
    }));
  };

  const handleColorImageUpload = async (e, variantIndex) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const existingCount = formData.colorVariants[variantIndex]?.images?.length || 0;
    if (files.length + existingCount > 6) {
      alert('Maximum 6 images per color variant');
      return;
    }
    setUploadingColorIndex(variantIndex);
    try {
      const formDataToSend = new FormData();
      files.forEach(file => formDataToSend.append('images', file));
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/products/upload-images`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataToSend
      });
      const data = await response.json();
      if (response.ok) {
        setFormData(prev => {
          const updated = [...prev.colorVariants];
          updated[variantIndex] = {
            ...updated[variantIndex],
            images: [...updated[variantIndex].images, ...data.imageUrls]
          };
          return { ...prev, colorVariants: updated };
        });
        e.target.value = '';
      } else {
        alert('Error uploading images: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      alert('Error uploading images: ' + error.message);
    } finally {
      setUploadingColorIndex(null);
    }
  };

  const removeColorImage = (variantIndex, imgIndex) => {
    setFormData(prev => {
      const updated = [...prev.colorVariants];
      updated[variantIndex] = {
        ...updated[variantIndex],
        images: updated[variantIndex].images.filter((_, i) => i !== imgIndex)
      };
      return { ...prev, colorVariants: updated };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        originalPrice: formData.originalPrice ? parseFloat(formData.originalPrice) : undefined,
        stock: parseInt(formData.stock),
        subcategories: formData.subcategories ? formData.subcategories.split(',').map(s => s.trim()) : [],
        // Strip _id from each variant (and their images) to avoid Mongoose subdocument conflicts
        colorVariants: formData.colorVariants.map(({ _id, __v, ...rest }) => ({
          colorName: rest.colorName,
          colorHex: rest.colorHex,
          images: rest.images || []
        }))
      };

      console.log('Submitting product data:', productData);
      console.log('Images in formData:', formData.images);

      const url = editingProduct 
        ? `${API_BASE}/products/${editingProduct._id}`
        : `${API_BASE}/products`;
      
      const method = editingProduct ? 'PUT' : 'POST';

      const token = localStorage.getItem('adminToken');
      const response = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(productData)
      });

      if (response.ok) {
        alert(editingProduct ? 'Product updated!' : 'Product created!');
        resetForm();
        fetchProducts();
      } else {
        const error = await response.json();
        alert('Error: ' + error.message);
      }
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error saving product');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    const subcats = product.subcategories || [];
    setSelectedSubcategories(subcats);
    setFormData({
      name: product.name,
      category: product.category,
      subcategories: subcats.join(', '),
      categoryLabel: product.categoryLabel || '',
      price: product.price.toString(),
      originalPrice: product.originalPrice?.toString() || '',
      description: product.description || '',
      images: product.images || [],
      badge: product.badge || '',
      gradient: product.gradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      stock: product.stock,
      isActive: product.isActive,
      isFeatured: product.isFeatured,
      showcaseSection: product.showcaseSection || '',
      sizeType: product.sizeType || 'NONE',
      availableSizes: product.availableSizes || [],
      colorVariants: product.colorVariants || []
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/products/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Product deleted!');
        fetchProducts();
      } else {
        alert('Error deleting product');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Error deleting product');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      subcategories: '',
      categoryLabel: '',
      price: '',
      originalPrice: '',
      description: '',
      images: [],
      badge: '',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      stock: 0,
      isActive: true,
      isFeatured: false,
      showcaseSection: '',
      sizeType: 'NONE',
      availableSizes: [],
      colorVariants: []
    });
    setSelectedSubcategories([]);
    setSelectedFiles([]);
    setNewColor({ colorName: '', colorHex: '#000000' });
    setEditingProduct(null);
    setShowForm(false);
  };

  return (
    <div className="admin-panel">
      <AdminNav />

      <div className="manager-container">
      <div className="manager-header">
        <h2>Products Management</h2>
        <button 
          className="btn-primary" 
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : '+ Add Product'}
        </button>
      </div>

      {showForm && (
        <div className="form-card">
          <h3>{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Product Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Designer Handbag"
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Category</option>
                  <option value="Women">Women</option>
                  <option value="Men">Men</option>
                  <option value="Kids">Kids</option>
                  <option value="Bags & Luggage">Bags & Luggage</option>
                  <option value="Beauty & Personal Care">Beauty & Personal Care</option>
                  <option value="Electronics & Gadgets">Electronics & Gadgets</option>
                  <option value="Home & Living">Home & Living</option>
                  <option value="Sports & Outdoors">Sports & Outdoors</option>
                  <option value="Unisex">Unisex</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Subcategories</label>
                {formData.category && subcategoryOptions[formData.category] ? (
                  <div className="subcategory-checkboxes">
                    {subcategoryOptions[formData.category].map(subcat => (
                      <label key={subcat} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedSubcategories.includes(subcat)}
                          onChange={() => handleSubcategoryToggle(subcat)}
                        />
                        <span>{subcat}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="form-hint">Select a category first to see subcategory options</p>
                )}
              </div>

              <div className="form-group">
                <label>Category Label</label>
                <input
                  type="text"
                  name="categoryLabel"
                  value={formData.categoryLabel}
                  onChange={handleInputChange}
                  placeholder="e.g., Bags & Accessories"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Price *</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  step="0.01"
                  min="0"
                  placeholder="299.99"
                />
              </div>

              <div className="form-group">
                <label>Original Price</label>
                <input
                  type="number"
                  name="originalPrice"
                  value={formData.originalPrice}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  placeholder="399.99"
                />
              </div>

              <div className="form-group">
                <label>Stock</label>
                <input
                  type="number"
                  name="stock"
                  value={formData.stock}
                  onChange={handleInputChange}
                  min="0"
                  placeholder="10"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                placeholder="Product description"
              />
            </div>

            <div className="form-group">
              <label>Product Images (Max 6)</label>
              <div className="image-upload-section">
                <div className="image-input-group">
                  <input
                    type="file"
                    id="productImages"
                    accept="image/*"
                    multiple
                    onChange={handleImageSelect}
                    disabled={formData.images.length >= 6 || uploadingImages}
                  />
                  {uploadingImages && (
                    <div className="upload-progress">
                      <div className="spinner"></div>
                      <span>Uploading...</span>
                    </div>
                  )}
                </div>
                
                {formData.images.length > 0 && (
                  <div className="uploaded-images">
                    {formData.images.map((img, index) => {
                      // Handle both Cloudinary URLs and local paths
                      const imageUrl = img.startsWith('http') ? img : `${API_BASE.replace('/api', '')}${img}`;
                      return (
                        <div key={index} className="image-preview">
                          <img src={imageUrl} alt={`Product ${index + 1}`} />
                        <button
                          type="button"
                          className="remove-image"
                          onClick={() => handleRemoveImage(index)}
                        >
                          ×
                        </button>
                          {index === 0 && <span className="primary-badge">Primary</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="form-hint">
                  {uploadingImages ? 'Uploading images...' : `${formData.images.length}/6 images uploaded. First image will be the primary image.`}
                </p>
              </div>
            </div>

            {/* Color Variants Section */}
            <div className="form-group">
              <label>Color Variants</label>
              <div className="color-variants-section">
                <div className="add-color-form">
                  <input
                    type="text"
                    placeholder="Color name (e.g., Red)"
                    value={newColor.colorName}
                    onChange={e => setNewColor(prev => ({ ...prev, colorName: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addColorVariant())}
                  />
                  <input
                    type="color"
                    value={newColor.colorHex}
                    onChange={e => setNewColor(prev => ({ ...prev, colorHex: e.target.value }))}
                    title="Pick color"
                  />
                  <button type="button" className="btn-add-color" onClick={addColorVariant}>
                    + Add Color
                  </button>
                </div>

                {formData.colorVariants.length === 0 && (
                  <p className="form-hint">No color variants added. Add colors to let customers filter images by color.</p>
                )}

                {formData.colorVariants.map((variant, vIdx) => (
                  <div key={vIdx} className="color-variant-item">
                    <div className="color-variant-header">
                      <div
                        className="color-swatch-admin"
                        style={{ backgroundColor: variant.colorHex }}
                      />
                      <span className="color-variant-name">{variant.colorName}</span>
                      <span className="color-variant-hex">{variant.colorHex}</span>
                      <button
                        type="button"
                        className="btn-remove-color"
                        onClick={() => removeColorVariant(vIdx)}
                      >
                        Remove
                      </button>
                    </div>

                    <div className="color-image-upload">
                      <p className="color-image-upload-label">Images for this color ({variant.images.length}/6):</p>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={e => handleColorImageUpload(e, vIdx)}
                        disabled={uploadingColorIndex === vIdx || variant.images.length >= 6}
                      />
                      {uploadingColorIndex === vIdx && (
                        <div className="upload-progress">
                          <div className="spinner"></div>
                          <span>Uploading...</span>
                        </div>
                      )}
                      {variant.images.length > 0 && (
                        <div className="color-images-preview">
                          {variant.images.map((img, imgIdx) => {
                            const url = img.startsWith('http') ? img : `${API_BASE.replace('/api', '')}${img}`;
                            return (
                              <div key={imgIdx} className="color-img-thumb">
                                <img src={url} alt={`${variant.colorName} ${imgIdx + 1}`} />
                                <button
                                  type="button"
                                  className="color-img-remove"
                                  onClick={() => removeColorImage(vIdx, imgIdx)}
                                >
                                  ×
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Badge</label>
                <select
                  name="badge"
                  value={formData.badge}
                  onChange={handleInputChange}
                >
                  <option value="">None</option>
                  <option value="NEW">NEW</option>
                  <option value="SALE">SALE</option>
                  <option value="HOT">HOT</option>
                  <option value="LIMITED">LIMITED</option>
                </select>
              </div>

              <div className="form-group">
                <label>Gradient</label>
                <input
                  type="text"
                  name="gradient"
                  value={formData.gradient}
                  onChange={handleInputChange}
                  placeholder="linear-gradient(...)"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Size Type</label>
              <select
                name="sizeType"
                value={formData.sizeType}
                onChange={handleSizeTypeChange}
              >
                <option value="NONE">None</option>
                <option value="CLOTHING">Clothing</option>
                <option value="SHOES">Shoes</option>
              </select>
            </div>

            {formData.sizeType === 'CLOTHING' && (
              <div className="form-group">
                <label>Available Sizes</label>
                <div className="subcategory-checkboxes">
                  {['S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'].map(size => (
                    <label key={size} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.availableSizes.includes(size)}
                        onChange={() => handleSizeToggle(size)}
                      />
                      <span>{size}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {formData.sizeType === 'SHOES' && (
              <div className="form-group">
                <label>Available Sizes</label>
                <div className="subcategory-checkboxes">
                  {['35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45'].map(size => (
                    <label key={size} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.availableSizes.includes(size)}
                        onChange={() => handleSizeToggle(size)}
                      />
                      <span>{size}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="form-group-checkboxes">
              <label>
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                />
                Active
              </label>
              <label>
                <input
                  type="checkbox"
                  name="isFeatured"
                  checked={formData.isFeatured}
                  onChange={handleInputChange}
                />
                Featured
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-submit" disabled={loading}>
                {loading ? 'Saving...' : (editingProduct ? 'Update Product' : 'Create Product')}
              </button>
              <button type="button" className="btn-cancel" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="products-list">
        {products.length === 0 ? (
          <p className="no-items">No products yet. Create your first one!</p>
        ) : (
          <>
            {/* Desktop table */}
            <table className="products-table desktop-only">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Sizes</th>
                  <th>Badge</th>
                  <th>Showcase</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map(product => (
                  <tr key={product._id}>
                    <td className="product-name">{product.name}</td>
                    <td>{product.categoryLabel || product.category}</td>
                    <td className="product-price">
                      ৳{product.price}
                      {product.originalPrice && (
                        <span className="original-price">৳{product.originalPrice}</span>
                      )}
                    </td>
                    <td>{product.stock}</td>
                    <td>
                      {product.sizeType && product.sizeType !== 'NONE' ? (
                        <span className="badge badge-size">
                          {product.sizeType}: {product.availableSizes?.join(', ') || 'None'}
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      {product.badge && (
                        <span className="badge badge-product">{product.badge}</span>
                      )}
                    </td>
                    <td>
                      {product.showcaseSection && product.showcaseSection.length > 0 ? (
                        <span className="badge badge-showcase">
                          {(Array.isArray(product.showcaseSection)
                            ? product.showcaseSection.join(', ')
                            : product.showcaseSection
                          ).split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      <span className={`badge ${product.isActive ? 'badge-active' : 'badge-inactive'}`}>
                        {product.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {product.isFeatured && (
                        <span className="badge badge-featured">Featured</span>
                      )}
                    </td>
                    <td className="table-actions">
                      <button className="btn-edit-sm" onClick={() => handleEdit(product)}>Edit</button>
                      <button className="btn-delete-sm" onClick={() => handleDelete(product._id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile cards */}
            <div className="product-cards mobile-only">
              {products.map(product => (
                <div className="product-mobile-card" key={product._id}>
                  <div className="pmc-header">
                    <span className="pmc-name">{product.name}</span>
                    <span className={`badge ${product.isActive ? 'badge-active' : 'badge-inactive'}`}>
                      {product.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="pmc-body">
                    <div className="pmc-row">
                      <span className="pmc-label">Category</span>
                      <span className="pmc-value">{product.categoryLabel || product.category}</span>
                    </div>
                    <div className="pmc-row">
                      <span className="pmc-label">Price</span>
                      <span className="pmc-value product-price">
                        ৳{product.price}
                        {product.originalPrice && (
                          <s className="pmc-original">৳{product.originalPrice}</s>
                        )}
                      </span>
                    </div>
                    <div className="pmc-row">
                      <span className="pmc-label">Stock</span>
                      <span className="pmc-value">{product.stock}</span>
                    </div>
                    {product.sizeType && product.sizeType !== 'NONE' && (
                      <div className="pmc-row">
                        <span className="pmc-label">Sizes</span>
                        <span className="pmc-value">
                          <span className="badge badge-size">
                            {product.sizeType}: {product.availableSizes?.join(', ') || 'None'}
                          </span>
                        </span>
                      </div>
                    )}
                    <div className="pmc-tags">
                      {product.badge && <span className="badge badge-product">{product.badge}</span>}
                      {product.isFeatured && <span className="badge badge-featured">Featured</span>}
                      {product.showcaseSection && product.showcaseSection.length > 0 && (
                        <span className="badge badge-showcase">
                          {(Array.isArray(product.showcaseSection)
                            ? product.showcaseSection.join(', ')
                            : product.showcaseSection
                          ).split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="pmc-actions">
                    <button className="btn-edit-sm" onClick={() => handleEdit(product)}>✏️ Edit</button>
                    <button className="btn-delete-sm" onClick={() => handleDelete(product._id)}>🗑 Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
    </div>
  );
};

export default ProductsManager;
